/**
 * Config centrale des modules admin.
 *
 * Inspiré du template Sakinea (adminModules.ts) — centralise pour que la nav
 * admin (dropdown header + sous-nav) tire d'une seule source de vérité.
 *
 * Pour ajouter/retirer un module : édite ce fichier uniquement.
 */
import { Tag, Inbox, Building2, Activity, BarChart3, Mail, ShieldCheck, Bell, Briefcase, MessageSquare, LineChart, Users as UsersIcon, FileText, MessageCircle, Home, Phone, ScrollText } from "lucide-react";

/**
 * Catalogue plat (sert au routing + résolution path → module).
 * `perm` = clé de permission RBAC requise pour voir/utiliser le module (cf.
 * rbac_spec.md §1). Pas de `perm` = toujours autorisé (ex. "Mon compte").
 * @type {Array<{id, label, path, icon, group, perm?, enabled?}>}
 */
export const ADMIN_MODULES = [
    { id: "quotes",           label: "Devis",                path: "/admin/devis",         icon: Inbox,       group: "leads", perm: "quotes" },
    { id: "cee-leads",        label: "Leads CEE & Solaire",  path: "/admin/cee-leads",     icon: Building2,   group: "leads", perm: "cee_leads" },
    { id: "isolation-leads",  label: "Leads Isolation/PAC",  path: "/admin/isolation-leads", icon: Home,      group: "leads", perm: "isolation_leads" },
    { id: "callbacks",        label: "Rappels demandés",     path: "/admin/callbacks",     icon: Phone,       group: "leads", perm: "callbacks" },
    { id: "contact-messages", label: "Messages contact",     path: "/admin/contact-messages", icon: MessageSquare, group: "leads", perm: "contact_messages" },
    { id: "mail",             label: "Boîte mail",           path: "/admin/mail",          icon: Mail,        group: "comms", perm: "mail" },
    { id: "partner-offers",   label: "Offres partenaires",   path: "/admin/offres-partenaires", icon: Tag,   group: "data", perm: "partner_offers" },
    { id: "suppliers",        label: "Fournisseurs",         path: "/admin/fournisseurs",  icon: Building2,   group: "data", perm: "suppliers" },
    { id: "clients",          label: "Clients",              path: "/admin/clients",       icon: UsersIcon,   group: "data", perm: "clients" },
    { id: "analytics-global", label: "Statistiques",         path: "/admin/analytics",     icon: BarChart3,   group: "analytics", perm: "analytics" },
    { id: "analytics-engine", label: "Analytics comparateur", path: "/admin/analytics-ia", icon: Activity,    group: "analytics", perm: "analytics" },
    { id: "content",          label: "Contenu du site",      path: "/admin/contenu",       icon: FileText,    group: "settings", perm: "content" },
    { id: "team",             label: "Équipe",               path: "/admin/equipe",        icon: ShieldCheck, group: "settings", perm: "team" },
    { id: "notifications",    label: "Notifications",        path: "/admin/notifications", icon: Bell,         group: "settings", perm: "notifications" },
    { id: "whatsapp",         label: "WhatsApp",             path: "/admin/whatsapp",      icon: MessageCircle, group: "settings", perm: "whatsapp" },
    { id: "audit",            label: "Journal d'audit",      path: "/admin/audit",         icon: ScrollText,   group: "settings", perm: "audit" },
    { id: "account",          label: "Mon compte",           path: "/admin/compte",        icon: ShieldCheck,  group: "settings" },
];

/**
 * Groupes pour la barre latérale (ordre = ordre d'affichage).
 */
export const ADMIN_GROUPS = [
    { id: "leads",     label: "Prospects" },
    { id: "comms",     label: "Communication" },
    { id: "data",      label: "Données" },
    { id: "analytics", label: "Analytics" },
    { id: "settings",  label: "Réglages" },
];

/**
 * Un module est visible s'il n'a pas de `perm` (ex. "Mon compte") ou si
 * `hasPerm(perm)` l'autorise. `hasPerm` par défaut laisse tout passer (compat
 * legacy / appels sans contexte auth).
 */
function isModuleAllowed(m, hasPerm) {
    return m.enabled !== false && (!m.perm || hasPerm(m.perm));
}

/** Modules regroupés par groupe, dans l'ordre de ADMIN_GROUPS (pour la sidebar). */
export function getAdminGroupedNav(hasPerm = () => true) {
    return ADMIN_GROUPS.map((g) => ({
        ...g,
        modules: ADMIN_MODULES.filter((m) => m.group === g.id && isModuleAllowed(m, hasPerm)),
    })).filter((g) => g.modules.length > 0);
}

/**
 * Premier module autorisé (dans l'ordre de déclaration de ADMIN_MODULES), utilisé
 * comme cible de repli quand l'URL courante pointe vers un module interdit.
 * "Mon compte" (sans perm) sert de filet de sécurité ultime.
 */
export function getFirstAllowedModulePath(hasPerm = () => true) {
    const mod = ADMIN_MODULES.find((m) => m.id !== "account" && isModuleAllowed(m, hasPerm));
    return mod ? mod.path : "/admin/compte";
}

/**
 * Groupes qui restent à plat dans la nav (chaque module = un pill individuel).
 */
const FLAT_GROUPS = ["leads", "comms", "analytics"];

/**
 * Groupes qui sont regroupés dans un dropdown.
 * Format : { id: groupId, label: "Libellé du dropdown" }
 */
const DROPDOWN_GROUPS = [
    { id: "settings", label: "Réglages" },
];


/**
 * Nav admin pour le header :
 *  - Les modules des groupes FLAT_GROUPS sortent comme pills individuels
 *  - Les modules des DROPDOWN_GROUPS sont regroupés dans un seul dropdown
 *  - Si un dropdown n'a qu'un module, il devient un pill direct
 *
 * @returns {Array<{to?, label, icon?, children?: Array<{to, label}>}>}
 */
export function getAdminNav(hasPerm = () => true) {
    const out = [];
    // 1) Items à plat (leads, comms, analytics)
    for (const groupId of FLAT_GROUPS) {
        const mods = ADMIN_MODULES.filter((m) => m.group === groupId && isModuleAllowed(m, hasPerm));
        for (const m of mods) {
            out.push({ to: m.path, label: m.label });
        }
    }
    // 2) Dropdowns (settings → "Réglages")
    for (const g of DROPDOWN_GROUPS) {
        const mods = ADMIN_MODULES.filter((m) => m.group === g.id && isModuleAllowed(m, hasPerm));
        if (mods.length === 0) continue;
        if (mods.length === 1) {
            out.push({ to: mods[0].path, label: mods[0].label });
        } else {
            out.push({
                label: g.label,
                children: mods.map((m) => ({ to: m.path, label: m.label })),
            });
        }
    }
    return out;
}


/** Renvoie le module actif d'après le pathname courant (ou null). */
export function findActiveModule(pathname) {
    return ADMIN_MODULES.find((m) => m.path === pathname) ?? null;
}
